import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, 
  ArrowLeft, 
  CheckCircle2, 
  ShieldCheck, 
  Truck, 
  CreditCard, 
  MapPin, 
  Phone, 
  User, 
  FileText, 
  ExternalLink,
  MessageCircle,
  AlertCircle,
  UserCheck,
  Smartphone,
  AlertTriangle,
  Lock,
  Sparkles
} from 'lucide-react';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';
import { ThaiAddress } from '../data/thaiAddresses';
import { searchThaiAddress } from '../lib/thaiAddressesDb';
import { db, collection, query, where, getDocs, doc, secureGetDoc } from '../firebase';
import { PinVerificationModal } from './PinVerificationModal';
import { ThaiQrPaymentBadge } from './payment/ThaiQrPaymentBadge';
import { 
  subscribePaymentSettings, 
  PaymentMethodsSettings, 
  getCachedPaymentSettings 
} from '../lib/paymentSettings';
import { 
  getOrCreateDeviceId, 
  checkCheckoutPinRequirement, 
  recordDeviceOrderSuccess, 
  getUserDeviceRecord,
  normalizeUserKey,
  PinRequirementCheck
} from '../lib/deviceSecurity';

interface CheckoutPageViewProps {
  checkoutSlug: string; // e.g. "MEM1234-ORD20260914-A82K" or "ORD20260914-A82K"
  currentUser?: any;
  onOpenAuth?: () => void;
  onNavigateHome: () => void;
}

export const CheckoutPageView: React.FC<CheckoutPageViewProps> = ({
  checkoutSlug,
  currentUser,
  onOpenAuth,
  onNavigateHome
}) => {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const [error, setError] = useState<string>('');

  // Form states
  const [fullName, setFullName] = useState(() => localStorage.getItem('giff_default_fullname') || '');
  const [phone, setPhone] = useState(() => localStorage.getItem('giff_default_phone') || '');
  const [address1, setAddress1] = useState(() => localStorage.getItem('giff_default_address1') || '');
  const [address2, setAddress2] = useState(() => localStorage.getItem('giff_default_address2') || '');
  const [address3, setAddress3] = useState(() => localStorage.getItem('giff_default_address3') || '');
  const [notes, setNotes] = useState('');
  const [paymentSettings, setPaymentSettings] = useState<PaymentMethodsSettings>(() => getCachedPaymentSettings());
  const [paymentMethod, setPaymentMethod] = useState<'PROMPTPAY' | 'BANK_TRANSFER' | 'COD' | 'CREDIT_CARD'>('COD');
  const [slipImage, setSlipImage] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [lineNotificationSent, setLineNotificationSent] = useState(false);

  // PIN Gate State
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pinCheckData, setPinCheckData] = useState<PinRequirementCheck | null>(null);

  // Address Autocomplete suggestions states
  const [addressSuggestions, setAddressSuggestions] = useState<ThaiAddress[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeFocusField, setActiveFocusField] = useState<'part2' | 'part3' | null>(null);

  const currentDeviceId = getOrCreateDeviceId();

  const handlePart2Change = async (val: string) => {
    setAddress2(val);
    setActiveFocusField('part2');
    if (val.trim().length >= 2) {
      const res = await searchThaiAddress(val);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePart3Change = async (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    setAddress3(cleaned);
    setActiveFocusField('part3');
    if (cleaned.length >= 2) {
      const res = await searchThaiAddress(cleaned);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectAddress = (item: ThaiAddress) => {
    setAddress2(`ต.${item.s} อ.${item.d} จ.${item.p}`);
    setAddress3(item.z);
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const [pullingAddress, setPullingAddress] = useState(false);
  const [pullMessage, setPullMessage] = useState('');

  const parseThaiAddress = (addressStr: string) => {
    let addr1 = '';
    let addr2 = '';
    let addr3 = '';

    const trimmed = addressStr.trim();
    const postalMatch = trimmed.match(/\b\d{5}\b$/);
    if (postalMatch) {
      addr3 = postalMatch[0];
      const withoutPostal = trimmed.substring(0, postalMatch.index).trim();
      
      const parts = withoutPostal.split(/\s+/);
      if (parts.length >= 3) {
        addr1 = parts.slice(0, Math.max(1, parts.length - 2)).join(' ');
        addr2 = parts.slice(Math.max(1, parts.length - 2)).join(' ');
      } else if (parts.length === 2) {
        addr1 = parts[0];
        addr2 = parts[1];
      } else {
        addr1 = withoutPostal;
      }
    } else {
      addr1 = trimmed;
    }

    return { addr1, addr2, addr3 };
  };

  // Parse Member ID and Order ID
  const parts = checkoutSlug.split('-');
  const parsedMemberId = parts.length > 2 ? parts[0] : (order?.memberId || 'MEMBER');
  const parsedOrderId = parts.length > 2 ? parts.slice(1).join('-') : checkoutSlug;

  const effectiveUserKey = normalizeUserKey(
    currentUser?.customerId || 
    currentUser?.memberId || 
    parsedMemberId || 
    order?.customerId || 
    order?.memberId || 
    phone || 
    'GUEST'
  );

  const deviceRecord = getUserDeviceRecord(effectiveUserKey, currentDeviceId);
  const orderCountOnDevice = deviceRecord ? deviceRecord.orderCount : 0;

  const handlePullMemberAddress = async () => {
    if (!parsedMemberId || parsedMemberId === 'MEMBER') {
      alert('ไม่พบรหัสสมาชิกที่เชื่อมโยงกับคำสั่งซื้อนี้');
      return;
    }
    setPullingAddress(true);
    setPullMessage('');
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('customerId', '==', parsedMemberId.trim()));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userId = userDoc.id;
        
        const profileDocRef = doc(db, 'users', userId, 'private', 'profile');
        const profileSnap = await secureGetDoc(profileDocRef, `users/${userId}/private/profile`);
        if (profileSnap && profileSnap.exists()) {
          const profileData = profileSnap.data() as any;
          const memberName = profileData.fullName || '';
          const memberPhone = profileData.phoneNumber || '';
          const memberAddress = profileData.shippingAddress || '';

          if (memberAddress) {
            const { addr1, addr2, addr3 } = parseThaiAddress(memberAddress);
            setAddress1(addr1);
            setAddress2(addr2);
            setAddress3(addr3);
            if (memberName && !fullName) {
              setFullName(memberName);
            }
            if (memberPhone && !phone) {
              setPhone(memberPhone);
            }
            setPullMessage('ดึงข้อมูลที่อยู่สำเร็จ!');
            setTimeout(() => setPullMessage(''), 3000);
          } else {
            alert('ไม่พบที่อยู่จัดส่งในข้อมูลสมาชิกท่านนี้');
          }
        } else {
          alert('ไม่พบข้อมูลโปรไฟล์สมาชิกท่านนี้');
        }
      } else {
        alert(`ไม่พบสมาชิกในระบบที่มีรหัสสมาชิก: ${parsedMemberId}`);
      }
    } catch (err) {
      console.error('Error pulling address:', err);
      alert('เกิดข้อผิดพลาดในการดึงข้อมูลจากระบบ');
    } finally {
      setPullingAddress(false);
    }
  };

  // Synchronize state with localStorage
  useEffect(() => {
    localStorage.setItem('giff_default_fullname', fullName);
  }, [fullName]);

  useEffect(() => {
    localStorage.setItem('giff_default_phone', phone);
  }, [phone]);

  useEffect(() => {
    localStorage.setItem('giff_default_address1', address1);
  }, [address1]);

  useEffect(() => {
    localStorage.setItem('giff_default_address2', address2);
  }, [address2]);

  useEffect(() => {
    localStorage.setItem('giff_default_address3', address3);
  }, [address3]);

  // Fetch Order Details
  useEffect(() => {
    const fetchDraftOrder = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/orders/draft/${checkoutSlug}`);
        const data = await res.json();
        if (res.ok && data.order) {
          setOrder(data.order);
          
          const localName = localStorage.getItem('giff_default_fullname');
          const localPhone = localStorage.getItem('giff_default_phone');
          const localAddr1 = localStorage.getItem('giff_default_address1');
          const localAddr2 = localStorage.getItem('giff_default_address2');
          const localAddr3 = localStorage.getItem('giff_default_address3');

          if (localName) setFullName(localName);
          else if (data.order.customerName) setFullName(data.order.customerName);

          if (localPhone) setPhone(localPhone);
          else if (data.order.phone) setPhone(data.order.phone);

          if (localAddr1) setAddress1(localAddr1);
          if (localAddr2) setAddress2(localAddr2);
          if (localAddr3) setAddress3(localAddr3);

          if (!localAddr1 && !localAddr2 && !localAddr3 && data.order.address) {
            setAddress1(data.order.address);
          }

          if (data.order.status === 'paid') setIsSuccess(true);
        } else {
          setError(data.error || 'ไม่พบข้อมูลคำสั่งซื้อ');
        }
      } catch (err: any) {
        setError('เกิดข้อผิดพลาดในการโหลดข้อมูลคำสั่งซื้อ กรุณาลองใหม่อีกครั้ง');
      } finally {
        setLoading(false);
      }
    };

    if (checkoutSlug) {
      fetchDraftOrder();
    }
  }, [checkoutSlug]);

  // Sync Dynamic Payment Methods from Admin Settings in Real-Time
  useEffect(() => {
    const unsub = subscribePaymentSettings((settings) => {
      setPaymentSettings(settings);
      setPaymentMethod((current) => {
        if (current === 'COD' && settings.cod) return 'COD';
        if (current === 'PROMPTPAY' && settings.qr) return 'PROMPTPAY';
        if (current === 'CREDIT_CARD' && settings.card) return 'CREDIT_CARD';

        // Auto fallback to first available active payment method
        if (settings.cod) return 'COD';
        if (settings.qr) return 'PROMPTPAY';
        if (settings.card) return 'CREDIT_CARD';
        return 'COD';
      });
    });

    return () => {
      unsub();
    };
  }, []);

  /**
   * Main Submit Action:
   * 1. Validate Form Fields
   * 2. Run Device & Threshold Security Rules
   *    - If total > 2,500 THB -> Prompt PIN
   *    - If new device (first order) -> Prompt PIN
   *    - If existing device & order 2+ & total <= 2,500 THB -> Auto Bypass PIN
   */
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !phone.trim() || !address1.trim() || !address2.trim() || !address3.trim()) {
      alert('กรุณากรอกชื่อ เบอร์โทรศัพท์ และที่อยู่จัดส่งให้ครบถ้วน');
      return;
    }

    if (!order) return;

    // Check PIN requirement
    const pinCheck = checkCheckoutPinRequirement({
      userKey: effectiveUserKey,
      totalAmount: order.totalAmount || 0,
    });

    if (pinCheck.requiresPin) {
      setPinCheckData(pinCheck);
      setIsPinModalOpen(true);
    } else {
      // Bypassed on trusted device (order 2+ and <= 2500 THB)
      await executeFinalOrderSubmission();
    }
  };

  /**
   * Final Order Submission after PIN verification or PIN bypass
   */
  const executeFinalOrderSubmission = async () => {
    const combinedAddress = `${address1.trim()} ${address2.trim()} ${address3.trim()}`;

    setSubmitting(true);
    try {
      const res = await fetch('/api/orders/submit-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: order.orderId,
          member_id: parsedMemberId,
          customer_name: fullName.trim(),
          phone: phone.trim(),
          address: combinedAddress,
          payment_method: paymentMethod,
          slip_url: slipImage,
          notes: notes.trim(),
          customer_email: order.customerEmail || order.email || currentUser?.email || '',
          email: order.customerEmail || order.email || currentUser?.email || '',
          device_id: currentDeviceId
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        // Record device order success in deviceSecurity ledger
        recordDeviceOrderSuccess({
          userKey: effectiveUserKey,
          orderId: order.orderId,
          totalAmount: order.totalAmount || 0,
          deviceId: currentDeviceId
        });

        try {
          window.dispatchEvent(new CustomEvent('order-created', { detail: data.order }));
        } catch (e) {}

        // If QR Payment (PromptPay) selected: redirect to QR payment processing page
        if (paymentMethod === 'PROMPTPAY' || data.relative_qr_processing_url) {
          const targetMemberId = parsedMemberId || currentUser?.customerId || order.customerId || '98939522';
          const targetSlug = `${targetMemberId}-${order.orderId}`;
          const targetUrl = data.relative_qr_processing_url || `/checkout/${targetSlug}/qr-payment-processing`;

          // Seamless SPA routing
          window.history.pushState({}, '', targetUrl);
          window.dispatchEvent(new Event('popstate'));
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        setIsSuccess(true);
        setLineNotificationSent(Boolean(data.line_notification_sent));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert(data.error || 'ไม่สามารถทำรายการได้ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err: any) {
      alert('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-[#2A59FE] rounded-full animate-spin mb-4" />
        <p className="text-slate-600 text-sm font-semibold">กำลังโหลดข้อมูลการชำระเงิน...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 mb-4">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-lg font-bold text-slate-800 mb-2">ไม่พบรายการสั่งซื้อ</h2>
        <p className="text-slate-500 text-xs mb-6 max-w-md">{error || 'คำสั่งซื้อนี้อาจถูกยกเลิกหรือไม่มีอยู่ในระบบ'}</p>
        <button
          type="button"
          onClick={onNavigateHome}
          className="px-6 py-2.5 bg-gradient-to-r from-[#2A59FE] to-[#3B66FE] text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 hover:opacity-95 transition-all cursor-pointer"
        >
          กลับหน้าหลัก
        </button>
      </div>
    );
  }

  // Success Confirmation Screen
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 py-10 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-blue-100 text-center animate-fadeIn">
          <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-100 shadow-xs">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 mb-1">
            บันทึกคำสั่งซื้อสำเร็จเรียบร้อย!
          </h2>
          <p className="text-xs text-slate-500 mb-6">
            รหัสสั่งซื้อ <span className="font-mono font-bold text-[#0E2C58]">#{parsedOrderId}</span>
          </p>

          <div className="bg-slate-50 rounded-2xl p-4 sm:p-5 border border-slate-100 text-left mb-6 space-y-2.5 text-xs text-slate-600">
            <div className="flex justify-between">
              <span className="text-slate-400">ชื่อผู้รับ:</span>
              <span className="font-bold text-slate-800">{fullName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">เบอร์โทรศัพท์:</span>
              <span className="font-bold text-slate-800">{phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">ที่อยู่จัดส่ง:</span>
              <span className="font-bold text-slate-800 text-right max-w-[65%]">{address1} {address2} {address3}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">ช่องทางชำระเงิน:</span>
              <span className="font-bold text-[#0E2C58]">เก็บเงินปลายทาง (COD)</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-2.5">
              <span className="font-bold text-slate-800">ยอดชำระสุทธิ:</span>
              <span className="font-black text-[#0E2C58] text-base">฿{order.totalAmount?.toLocaleString()}</span>
            </div>
            {order.totalPv > 0 && (
              <div className="flex justify-between text-indigo-700 bg-indigo-50/70 p-2 rounded-xl border border-indigo-100">
                <span className="font-bold">แต้มสะสม PV ที่ได้รับ:</span>
                <span className="font-black">{order.totalPv.toLocaleString()} PV</span>
              </div>
            )}
            <div className="flex justify-between text-[11px] text-slate-500 border-t border-slate-100 pt-2">
              <span>สถานะอุปกรณ์:</span>
              <span className="text-emerald-600 font-bold flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" />
                บันทึกรหัสอุปกรณ์ปลอดภัยแล้ว
              </span>
            </div>
          </div>

          {lineNotificationSent && (
            <div className="mb-6 p-3 bg-emerald-50/80 border border-emerald-200 rounded-2xl flex items-center justify-center gap-2 text-emerald-800 text-xs font-semibold">
              <MessageCircle className="w-4 h-4 text-[#06C755]" />
              <span>ส่งข้อมูลและใบแจ้งยอดสรุปเข้าสู่ LINE Official เรียบร้อยแล้วค่ะ</span>
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={onNavigateHome}
              className="w-full py-3.5 bg-gradient-to-r from-[#0E2C58] via-[#2A59FE] to-[#4F46E5] hover:opacity-95 text-white rounded-2xl text-xs font-bold shadow-lg shadow-blue-900/20 transition-all cursor-pointer"
            >
              กลับสู่หน้าหลัก GIFFFORYOU
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-10 px-3 sm:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Navigation Bar */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <button
            type="button"
            onClick={onNavigateHome}
            className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-[#2A59FE] transition-colors cursor-pointer bg-white px-3.5 py-2 rounded-xl border border-slate-200/80 shadow-2xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>กลับหน้าร้านค้า</span>
          </button>

          <div className="flex items-center gap-3">
            <img
              src={GIFFARINE_OFFICIAL_LOGO}
              alt="Giffarine Official Logo"
              className="h-7 w-auto object-contain"
              referrerPolicy="no-referrer"
            />
            <span className="text-[11px] font-bold text-[#2A59FE] bg-blue-50 border border-blue-200/60 px-2.5 py-1 rounded-lg">
              Official Checkout
            </span>
          </div>
        </div>

        {/* Security Device Banner */}
        <div className="mb-6 p-3.5 bg-white rounded-2xl border border-slate-200 shadow-2xs flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-[#2A59FE] flex items-center justify-center shrink-0 border border-blue-100">
              <Smartphone className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800">ระบบบันทึกรหัสอุปกรณ์อัตโนมัติ (Zero-Trust)</span>
                {orderCountOnDevice >= 1 ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    อุปกรณ์เดิม (ครั้งที่ {orderCountOnDevice + 1})
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    อุปกรณ์ใหม่ (สั่งซื้อครั้งแรก)
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {order?.totalAmount > 2500 ? (
                  <span className="text-amber-600 font-semibold">
                    * ยอดสั่งซื้อเกิน ฿2,500 บาท ระบบจะเรียกยืนยันรหัส PIN 6 หลักเพื่อความปลอดภัยสูงสุด
                  </span>
                ) : orderCountOnDevice >= 1 ? (
                  <span className="text-emerald-600 font-semibold">
                    * ใช้งานบนอุปกรณ์เดิมและยอดไม่เกิน ฿2,500 ระบบจะข้ามการยืนยัน PIN อัตโนมัติ
                  </span>
                ) : (
                  <span className="text-slate-500">
                    * สั่งซื้อครั้งแรกบนอุปกรณ์ใหม่ ระบบจะเรียกให้กรอกรหัส PIN 6 หลักเพื่อยืนยันตัวตน
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="font-mono text-[10px] text-slate-400 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200/60">
            ID: {currentDeviceId.slice(0, 16)}...
          </div>
        </div>

        {/* Main 2-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Delivery Form (7 Cols) */}
          <div className="lg:col-span-7 space-y-5">
            {/* 1. Customer Shipping Card */}
            <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
                <div className="flex items-center gap-2 text-[#0E2C58] font-black text-sm">
                  <MapPin className="w-4 h-4 text-[#2A59FE]" />
                  <h3>ข้อมูลที่อยู่จัดส่งสินค้า</h3>
                </div>

                {parsedMemberId && parsedMemberId !== 'MEMBER' && (
                  <button
                    type="button"
                    onClick={handlePullMemberAddress}
                    disabled={pullingAddress}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2A59FE] bg-blue-50 hover:bg-blue-100/80 px-3 py-1.5 rounded-xl border border-blue-200 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    <span>{pullingAddress ? 'กำลังดึงข้อมูล...' : 'ดึงที่อยู่จากรหัสสมาชิก'}</span>
                  </button>
                )}
              </div>

              {pullMessage && (
                <div className="mb-4 p-2.5 rounded-xl bg-emerald-50 border border-emerald-100 text-xs text-emerald-700 font-bold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{pullMessage}</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      ชื่อ-นามสกุล ผู้รับสินค้า <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="เช่น สมชาย ใจดี"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-base sm:text-xs focus:border-pink-500 focus:outline-none"
                      />
                      <User className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      เบอร์โทรศัพท์ติดต่อ <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="เช่น 0812345678"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-base sm:text-xs focus:border-pink-500 focus:outline-none"
                      />
                      <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    </div>
                  </div>
                </div>

                {/* 3-Part Address Inputs */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      1. บ้านเลขที่, หมู่บ้าน/อาคาร, ซอย, ถนน <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={address1}
                      onChange={(e) => setAddress1(e.target.value)}
                      placeholder="เช่น 123/45 หมู่ 6 ซอยสุขุมวิท 55 ถนนสุขุมวิท"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-base sm:text-xs focus:border-pink-500 focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Part 2: Tumbon / Amphoe / Province */}
                    <div className="relative">
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        2. ตำบล / อำเภอ / จังหวัด <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={address2}
                        onChange={(e) => handlePart2Change(e.target.value)}
                        onFocus={() => {
                          setActiveFocusField('part2');
                          if (address2.trim().length >= 2) handlePart2Change(address2);
                        }}
                        placeholder="พิมพ์ค้นหา เช่น คลองตัน"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-base sm:text-xs focus:border-pink-500 focus:outline-none"
                      />
                      {showSuggestions && activeFocusField === 'part2' && addressSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-y-auto bg-white border border-pink-100 rounded-xl shadow-xl divide-y divide-slate-100">
                          {addressSuggestions.map((item, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectAddress(item)}
                              className="w-full px-3.5 py-2.5 text-left text-xs text-slate-700 hover:bg-pink-50/60 flex items-start gap-2.5 transition-colors cursor-pointer"
                            >
                              <MapPin className="w-3.5 h-3.5 text-[#E60067] shrink-0 mt-0.5" />
                              <div>
                                <span className="font-bold text-slate-800">ต.{item.s} อ.{item.d} จ.{item.p}</span>
                                <p className="text-[10px] text-slate-400">รหัสไปรษณีย์: {item.z}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Part 3: Postal Code */}
                    <div className="relative">
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        3. รหัสไปรษณีย์ 5 หลัก <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={5}
                        value={address3}
                        onChange={(e) => handlePart3Change(e.target.value)}
                        onFocus={() => {
                          setActiveFocusField('part3');
                          if (address3.trim().length >= 2) handlePart3Change(address3);
                        }}
                        placeholder="เช่น 10110"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-base sm:text-xs focus:border-pink-500 focus:outline-none font-mono"
                      />
                      {showSuggestions && activeFocusField === 'part3' && addressSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-y-auto bg-white border border-pink-100 rounded-xl shadow-xl divide-y divide-slate-100">
                          {addressSuggestions.map((item, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectAddress(item)}
                              className="w-full px-3.5 py-2.5 text-left text-xs text-slate-700 hover:bg-pink-50/60 flex items-start gap-2.5 transition-colors cursor-pointer"
                            >
                              <MapPin className="w-3.5 h-3.5 text-[#E60067] shrink-0 mt-0.5" />
                              <div>
                                <span className="font-bold text-slate-800">{item.z}</span>
                                <p className="text-[10px] text-slate-400">ต.{item.s} อ.{item.d} จ.{item.p}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    หมายเหตุเพิ่มเติม (ถ้ามี)
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="เช่น ฝากไว้ที่ป้อมยาม, โทรแจ้งก่อนส่ง"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-base sm:text-xs focus:border-pink-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* 2. Payment Method Card */}
            <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-50 shadow-sm">
              <div className="flex items-center justify-between pb-2 mb-4 border-b border-pink-50">
                <div className="flex items-center gap-2 text-[#9D174D] font-black text-sm">
                  <CreditCard className="w-4 h-4 text-[#E60067]" />
                  <h3>ช่องทางการชำระเงิน</h3>
                </div>
                <span className="text-[11px] text-slate-400 font-medium">
                  {[paymentSettings.cod, paymentSettings.qr, paymentSettings.card].filter(Boolean).length > 1
                    ? 'เลือกช่องทางที่ต้องการ'
                    : 'ช่องทางที่เปิดรับชำระ'}
                </span>
              </div>

              <div className="space-y-3">
                {/* 1. เก็บเงินปลายทาง (COD) */}
                {paymentSettings.cod && (
                  <div
                    id="payment-method-cod"
                    onClick={() => setPaymentMethod('COD')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer select-none flex items-start gap-3.5 ${
                      paymentMethod === 'COD'
                        ? 'border-[#E60067] bg-pink-50/20 text-[#9D174D] shadow-2xs'
                        : 'border-slate-200 hover:border-pink-200 bg-white text-slate-700'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${
                      paymentMethod === 'COD'
                        ? 'bg-[#E60067] text-white shadow-sm shadow-pink-500/20'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      <Truck className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-bold text-slate-900">เก็บเงินปลายทาง (COD)</h4>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          paymentMethod === 'COD'
                            ? 'bg-pink-100 text-[#9D174D] border border-pink-200'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          COD
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                        ชำระเงินสดหรือสแกนจ่ายกับเจ้าหน้าที่ขนส่งเมื่อได้รับของ
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-1 transition-colors ${
                      paymentMethod === 'COD'
                        ? 'border-[#E60067] bg-[#E60067] text-white'
                        : 'border-slate-300 bg-white'
                    }`}>
                      {paymentMethod === 'COD' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </div>
                )}

                {/* 2. คิวอาร์เพย์เม้นท์ (THAI QR PAYMENT) */}
                {paymentSettings.qr && (
                  <div
                    id="payment-method-qr"
                    onClick={() => setPaymentMethod('PROMPTPAY')}
                    className={`p-3.5 sm:p-4 rounded-2xl border-2 transition-all cursor-pointer select-none flex items-start gap-3 sm:gap-3.5 ${
                      paymentMethod === 'PROMPTPAY'
                        ? 'border-[#E60067] bg-pink-50/20 text-[#9D174D] shadow-2xs'
                        : 'border-slate-200 hover:border-pink-200 bg-white text-slate-700'
                    }`}
                  >
                    {/* Official THAI QR PAYMENT Logo Badge */}
                    <div className="shrink-0 pt-0.5">
                      <div className="h-10 sm:h-11 w-[92px] sm:w-[100px] flex items-center justify-center rounded-xl overflow-hidden shadow-xs border border-[#00387A] bg-[#002D63]">
                        <ThaiQrPaymentBadge className="w-full h-full object-contain" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center justify-between gap-1.5">
                        <h4 className="text-sm font-bold text-slate-900">คิวอาร์เพย์เม้นท์ (THAI QR PAYMENT)</h4>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                          paymentMethod === 'PROMPTPAY'
                            ? 'bg-[#002D63] text-white shadow-2xs'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00A796]" />
                          พร้อมเพย์ ทุกธนาคาร
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                        สแกนชำระเงินผ่านระบบ Thai QR Code พร้อมเพย์ สะดวกรวดเร็ว ปลอดภัยด้วย Mobile Banking ทุกธนาคาร
                      </p>

                      {/* Bank support micro-tags when selected */}
                      {paymentMethod === 'PROMPTPAY' && (
                        <div className="mt-2.5 pt-2 border-t border-pink-200/60 flex flex-wrap items-center gap-1.5 text-[10px]">
                          <span className="font-semibold text-slate-700">สแกนจ่ายได้ทุกธนาคาร:</span>
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">KBANK</span>
                          <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-bold">SCB</span>
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-bold">BBL</span>
                          <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 font-bold">KTB</span>
                          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-bold">BAY</span>
                          <span className="px-1.5 py-0.5 rounded bg-pink-50 text-pink-700 border border-pink-200 font-bold">GSB</span>
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 font-medium">และทุกแอป</span>
                        </div>
                      )}
                    </div>

                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-1 transition-colors ${
                      paymentMethod === 'PROMPTPAY'
                        ? 'border-[#E60067] bg-[#E60067] text-white'
                        : 'border-slate-300 bg-white'
                    }`}>
                      {paymentMethod === 'PROMPTPAY' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </div>
                )}

                {/* 3. บัตรเครดิต/บัตรเดบิต (Credit/Debit Card) */}
                {paymentSettings.card && (
                  <div
                    id="payment-method-card"
                    onClick={() => setPaymentMethod('CREDIT_CARD')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer select-none flex items-start gap-3.5 ${
                      paymentMethod === 'CREDIT_CARD'
                        ? 'border-[#E60067] bg-pink-50/20 text-[#9D174D] shadow-2xs'
                        : 'border-slate-200 hover:border-pink-200 bg-white text-slate-700'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${
                      paymentMethod === 'CREDIT_CARD'
                        ? 'bg-[#E60067] text-white shadow-sm shadow-pink-500/20'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-bold text-slate-900">บัตรเครดิต/บัตรเดบิต (Credit/Debit Card)</h4>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          paymentMethod === 'CREDIT_CARD'
                            ? 'bg-pink-100 text-[#9D174D] border border-pink-200'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          Secure Card
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                        รองรับบัตรเครดิตและเดบิตชั้นนำทุกธนาคาร เช่น Visa, Mastercard และ JCB เพิ่มความหลากหลายในการรับเงิน
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-1 transition-colors ${
                      paymentMethod === 'CREDIT_CARD'
                        ? 'border-[#E60067] bg-[#E60067] text-white'
                        : 'border-slate-300 bg-white'
                    }`}>
                      {paymentMethod === 'CREDIT_CARD' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </div>
                )}

                {/* Fallback if no payment method enabled */}
                {!paymentSettings.cod && !paymentSettings.qr && !paymentSettings.card && (
                  <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 text-xs">
                    <p className="font-bold">ยังไม่ได้เปิดใช้งานช่องทางชำระเงิน</p>
                    <p className="mt-0.5 text-[11px] text-amber-700">กรุณาติดต่อผู้ดูแลระบบเพื่อเปิดใช้งานช่องทางการชำระเงิน</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Order Summary (5 Cols) */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200/80 shadow-sm sticky top-6">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
                <div className="flex items-center gap-2 text-[#0E2C58] font-black text-sm">
                  <ShoppingBag className="w-4 h-4 text-[#2A59FE]" />
                  <h3>สรุปรายการสั่งซื้อ</h3>
                </div>
                <span className="text-[11px] font-mono text-slate-400">
                  {parsedOrderId}
                </span>
              </div>

              {/* Items List */}
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1 mb-4">
                {order.items?.map((item: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50/60 border border-slate-100 text-xs">
                    {item.image ? (
                      <img src={item.image} alt={item.productName} className="w-12 h-12 rounded-lg object-cover bg-white border border-slate-200 shrink-0" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 font-bold text-xs shrink-0">
                        GIF
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 truncate">{item.productName}</p>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                        <span className="font-mono">รหัส {item.productCode}</span>
                        <span>•</span>
                        <span>จำนวน {item.quantity} ชิ้น</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="font-bold text-slate-900 block">฿{item.subtotal?.toLocaleString()}</span>
                      {item.pv > 0 && <span className="text-[10px] text-emerald-700 font-bold">{item.pv} PV</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Price Calculation */}
              <div className="space-y-2 text-xs text-slate-600 border-t border-slate-100 pt-3">
                <div className="flex justify-between">
                  <span>ยอดรวมสินค้า ({order.items?.reduce((a: number, b: any) => a + (b.quantity || 1), 0)} ชิ้น):</span>
                  <span className="font-semibold text-slate-800">฿{order.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>ค่าจัดส่ง:</span>
                  <span className={order.deliveryFee === 0 ? 'text-emerald-600 font-bold' : 'text-slate-800'}>
                    {order.deliveryFee === 0 ? 'ฟรี (โปรโมชั่น)' : `฿${order.deliveryFee}`}
                  </span>
                </div>
                {order.totalPv > 0 && (
                  <div className="flex justify-between text-emerald-800 bg-emerald-50/70 p-2 rounded-xl border border-emerald-200">
                    <span className="font-bold">แต้มสะสม PV ที่จะได้รับ:</span>
                    <span className="font-black font-mono">{order.totalPv.toLocaleString()} PV</span>
                  </div>
                )}
                <div className="pt-2 border-t border-slate-200 flex justify-between items-baseline text-slate-900">
                  <span className="text-sm font-black">ยอดชำระสุทธิ:</span>
                  <span className="text-2xl font-black text-[#2A59FE]">
                    ฿{order.totalAmount?.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmitOrder}
                disabled={submitting}
                className="w-full mt-5 py-3.5 px-4 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 transition-all active:scale-[0.98] disabled:opacity-50 cursor-pointer"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>{submitting ? 'กำลังบันทึกคำสั่งซื้อ...' : 'ยืนยันการชำระเงินและสั่งซื้อสินค้า'}</span>
              </button>

              <div className="mt-3 text-center">
                <span className="text-[10px] text-slate-400 flex items-center justify-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-emerald-500" />
                  ระบบความปลอดภัยและการรับประกันสินค้าของแท้ 100%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zero-Trust PIN Verification Modal */}
      <PinVerificationModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        user={currentUser}
        expectedPin={currentUser?.pinHash}
        title={pinCheckData?.title}
        description={pinCheckData?.description}
        securityReason={pinCheckData?.reason}
        orderAmount={order?.totalAmount}
        onSuccess={executeFinalOrderSubmission}
      />
    </div>
  );
};
